import { Band } from './primitives/Band';
import { Chip } from './primitives/Chip';
import { CheckRow } from './primitives/CheckRow';
import { MonoKicker } from './primitives/MonoKicker';

const deployments = [
    {
        badge: 'Default',
        badgeTone: 'sage',
        title: 'Private cloud',
        footer: 'Included in every engagement',
        body: 'Our managed environment. Isolated, encrypted at rest and in transit, and never used to train any model — ours or anyone else’s. Nothing for you to stand up or maintain.',
    },
    {
        badge: 'Dedicated',
        badgeTone: 'quiet',
        title: 'Your own cloud',
        footer: 'We build it with you',
        body: 'A private instance provisioned for your practice alone — your region, your retention rules, your keys. Our team stands it up and runs it.',
    },
    {
        badgeTone: 'quiet',
        badge: 'On-premises',
        title: 'Never leaves the building',
        footer: 'Talk to us about requirements',
        body: 'Mohair runs on hardware you own. Client data never reaches the cloud at all. For when the only acceptable answer is that it never left the premises.',
    },
] as const;

const assurances = [
    'HIPAA-ready architecture, BAA on every engagement',
    'Zero audio retention after the note is drafted',
    'Your notes never train shared models',
    'Full audit trail on every filed document',
];

export function SecurityBand() {
    return (
        <Band id="security" tone="oxblood">
            <div className="flex flex-col gap-12">
                <div className="flex max-w-2xl flex-col gap-4">
                    <MonoKicker data-reveal>Security & deployment</MonoKicker>
                    <h2
                        data-reveal
                        className="font-display text-[clamp(30px,4vw,40px)] leading-[1.15] text-balance"
                    >
                        Your data lives where you decide.
                    </h2>
                    <p
                        data-reveal
                        className="text-[clamp(16px,1.4vw,18px)] leading-[1.55] text-muted-foreground"
                    >
                        HIPAA compliance is the floor, not the pitch. The
                        question worth asking is where the records actually sit
                        — and because we do the deployment ourselves, the answer
                        can be whatever your practice needs it to be.
                    </p>
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-6">
                    {deployments.map((item) => (
                        <div
                            key={item.title}
                            data-reveal
                            className="flex flex-col gap-6 rounded-[16px] border border-border bg-card p-8"
                        >
                            <Chip
                                className="self-start"
                                tone={
                                    item.badgeTone === 'sage' ? 'sage' : 'quiet'
                                }
                            >
                                {item.badge}
                            </Chip>
                            <div className="flex flex-col gap-3">
                                <h3 className="font-display text-[26px] leading-[1.2]">
                                    {item.title}
                                </h3>
                                <p className="text-[16px] leading-[1.55] text-muted-foreground">
                                    {item.body}
                                </p>
                            </div>
                            <MonoKicker className="mt-auto pt-6">
                                {item.footer}
                            </MonoKicker>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-4">
                    {assurances.map((line) => (
                        <CheckRow key={line} data-reveal>
                            {line}
                        </CheckRow>
                    ))}
                </div>
            </div>
        </Band>
    );
}
